// Admin tips — log + reverse action.
//
// Reversing a tip debits the recipient's wallet, credits the sender's
// credit balance back, marks earnings row reversed, and notifies both.
// All atomic on the server in POST /admin/tips/:id/reverse.
import { useCallback, useEffect, useState } from "react";
import { Button } from "../../components/ui/Button";
import { Card, CardSubtle, CardTitle } from "../../components/ui/Card";
import { Input, Label, Select, Textarea } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { Spinner } from "../../components/ui/Spinner";
import { api } from "../../lib/api";

interface TipRow {
  id: string;
  from_user_id: string;
  to_user_id: string;
  amount_usd: number;
  post_id: string | null;
  comment_id: string | null;
  message: string | null;
  show_publicly: boolean;
  status: string;
  reversed_at: string | null;
  reversal_reason: string | null;
  translation_id: string | null;
  created_at: string;
  from_profile: {
    id: string;
    username: string;
    avatar_url: string | null;
  } | null;
  to_profile: {
    id: string;
    username: string;
    avatar_url: string | null;
  } | null;
  post_translations: {
    id: string;
    language_code: string;
    translator_id: string;
    translator_profile: { username: string } | null;
  } | null;
}

interface Stats {
  completed: { count: number; sum: number };
  reversed: { count: number; sum: number };
}

interface ListResponse {
  tips: TipRow[];
  total: number;
  stats: Stats;
}

function formatUsd(n: number): string {
  return `$${n.toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function contextLabel(tip: TipRow): string {
  if (tip.post_id) return "Post";
  if (tip.comment_id) return "Comment";
  return "Profile";
}

function statusColor(status: string): string {
  switch (status) {
    case "completed":
      return "var(--upvote, #22C55E)";
    case "reversed":
      return "var(--danger, #ef4444)";
    default:
      return "var(--text-secondary)";
  }
}

export default function AdminTipsPage() {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [contextFilter, setContextFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [reverseTarget, setReverseTarget] = useState<TipRow | null>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseSubmitting, setReverseSubmitting] = useState(false);
  const [reverseError, setReverseError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (contextFilter) params.set("context", contextFilter);
      if (debouncedSearch) params.set("search", debouncedSearch);
      params.set("limit", "100");
      const res = await api<ListResponse>(`/admin/tips?${params.toString()}`);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, contextFilter, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  async function submitReverse() {
    if (!reverseTarget) return;
    setReverseSubmitting(true);
    setReverseError(null);
    try {
      await api(`/admin/tips/${reverseTarget.id}/reverse`, {
        method: "POST",
        body: { reason: reverseReason.trim() },
      });
      setReverseTarget(null);
      setReverseReason("");
      await fetchList();
    } catch (err) {
      setReverseError(err instanceof Error ? err.message : "Reverse failed");
    } finally {
      setReverseSubmitting(false);
    }
  }

  const rows = data?.tips ?? [];

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.h1}>Tips</h1>
        <CardSubtle>
          All tips. Reversing a tip debits the recipient's wallet and
          restores the sender's credits. Use sparingly.
        </CardSubtle>
      </div>

      <div style={styles.statsRow}>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Completed (all-time)</CardSubtle>
          <div style={styles.statValue}>
            {data?.stats.completed.count ?? "—"}
          </div>
          <CardSubtle>
            {data ? formatUsd(data.stats.completed.sum) : ""}
          </CardSubtle>
        </Card>
        <Card style={{ flex: 1 }}>
          <CardSubtle>Reversed</CardSubtle>
          <div style={styles.statValue}>
            {data?.stats.reversed.count ?? "—"}
          </div>
          <CardSubtle>
            {data ? formatUsd(data.stats.reversed.sum) : ""}
          </CardSubtle>
        </Card>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div style={styles.filterRow}>
          <div style={{ flex: 1 }}>
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="reversed">Reversed</option>
            </Select>
          </div>
          <div style={{ flex: 1 }}>
            <Label>Context</Label>
            <Select
              value={contextFilter}
              onChange={(e) => setContextFilter(e.target.value)}
            >
              <option value="">All contexts</option>
              <option value="post">Post</option>
              <option value="comment">Comment</option>
              <option value="profile">Profile (direct)</option>
            </Select>
          </div>
          <div style={{ flex: 2 }}>
            <Label>Search user (sender or recipient)</Label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="username substring"
            />
          </div>
        </div>
      </Card>

      {loading ? (
        <Card>
          <div style={styles.loadingWrap}>
            <Spinner />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div style={styles.errorBar}>{error}</div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <CardTitle>No tips</CardTitle>
          <CardSubtle>Nothing matches the current filters.</CardSubtle>
        </Card>
      ) : (
        <Card padding={0}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHead}>
                <Th>From</Th>
                <Th>To</Th>
                <Th>Amount</Th>
                <Th>Context</Th>
                <Th>Message</Th>
                <Th>Status</Th>
                <Th>When</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} style={styles.tableRow}>
                  <Td>@{row.from_profile?.username ?? "(deleted)"}</Td>
                  <Td>@{row.to_profile?.username ?? "(deleted)"}</Td>
                  <Td>
                    <strong>{formatUsd(row.amount_usd)}</strong>
                  </Td>
                  <Td>
                    <span>{contextLabel(row)}</span>
                    {!row.show_publicly && (
                      <CardSubtle style={{ fontSize: 12 }}>private</CardSubtle>
                    )}
                    {row.post_translations && (
                      <CardSubtle
                        style={{
                          fontSize: 12,
                          color: "var(--accent)",
                          marginTop: 2,
                        }}
                      >
                        Translator share → @
                        {row.post_translations.translator_profile?.username ?? "?"}
                        {" ("}
                        {row.post_translations.language_code}
                        {")"}
                      </CardSubtle>
                    )}
                  </Td>
                  <Td>
                    {row.message ? (
                      <span
                        style={{
                          fontStyle: "italic",
                          color: "var(--text-secondary)",
                        }}
                      >
                        "
                        {row.message.length > 60
                          ? row.message.slice(0, 60) + "…"
                          : row.message}
                        "
                      </span>
                    ) : (
                      <CardSubtle style={{ fontSize: 12 }}>—</CardSubtle>
                    )}
                  </Td>
                  <Td>
                    <span style={{ color: statusColor(row.status) }}>
                      {row.status}
                    </span>
                    {row.reversal_reason && (
                      <CardSubtle
                        style={{
                          fontSize: 12,
                          fontStyle: "italic",
                          marginTop: 2,
                        }}
                      >
                        {row.reversal_reason}
                      </CardSubtle>
                    )}
                  </Td>
                  <Td>
                    <CardSubtle style={{ fontSize: 12 }}>
                      {formatDate(row.created_at)}
                    </CardSubtle>
                  </Td>
                  <Td>
                    {row.status === "completed" && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setReverseTarget(row)}
                      >
                        Reverse
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={reverseTarget !== null}
        onClose={() => !reverseSubmitting && setReverseTarget(null)}
        title={
          reverseTarget
            ? `Reverse ${formatUsd(reverseTarget.amount_usd)} tip from @${reverseTarget.from_profile?.username ?? ""} → @${reverseTarget.to_profile?.username ?? ""}`
            : ""
        }
      >
        <CardSubtle style={{ marginBottom: 12 }}>
          Debits the recipient's wallet, restores the sender's credits,
          and notifies both parties with the reason below. Cannot be
          undone.
        </CardSubtle>
        <Label>Reason</Label>
        <Textarea
          value={reverseReason}
          onChange={(e) => setReverseReason(e.target.value)}
          rows={3}
          placeholder="Briefly explain why this tip is being reversed"
        />
        {reverseError && (
          <div style={{ ...styles.errorBar, marginTop: 12 }}>
            {reverseError}
          </div>
        )}
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button
            variant="secondary"
            onClick={() => setReverseTarget(null)}
            disabled={reverseSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={submitReverse}
            disabled={reverseReason.trim().length < 3 || reverseSubmitting}
          >
            {reverseSubmitting ? "Reversing…" : "Reverse tip"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th style={styles.th}>{children}</th>;
}
function Td({ children }: { children: React.ReactNode }) {
  return <td style={styles.td}>{children}</td>;
}

const styles: Record<string, React.CSSProperties> = {
  header: { marginBottom: 16 },
  h1: { margin: 0, fontSize: 24, fontWeight: 700, color: "var(--text-primary)" },
  statsRow: { display: "flex", gap: 12, marginBottom: 16 },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: "var(--text-primary)",
    margin: "4px 0",
  },
  filterRow: { display: "flex", gap: 12 },
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 0",
  },
  errorBar: {
    background: "rgba(239,68,68,0.1)",
    color: "var(--danger, #ef4444)",
    padding: 12,
    borderRadius: "var(--radius-sm)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 14,
  },
  tableHead: {
    background: "rgba(255,255,255,0.02)",
    textAlign: "left",
  },
  tableRow: { borderTop: "1px solid var(--border)" },
  th: {
    padding: "10px 12px",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--text-secondary)",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  td: { padding: 12, verticalAlign: "top", color: "var(--text-primary)" },
};
